import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import type {
  CheckRunEnvironmentDataRootsResponseBody,
  ReplaceRunEnvironmentDataRootsRequestBody,
  ReplaceRunEnvironmentDataRootsResponseBody,
  RunEnvironmentDataRoot,
  RunEnvironmentDataRootsImpactResponseBody,
} from "common";

/**
 * #842's data root routes plus the Check folders and usage routes, all under
 * backend/src/routes/mountAllowlist.ts. Mounted at /v1/run-environment (routes/_base.ts), not
 * under /user/v1. Owner or platform admin only: a 403 means the viewer does not own the
 * environment.
 */
export const RUN_ENVIRONMENT_BASE = "/api/v1/run-environment";

export const dataRootsQueryKey = (
  environmentId?: number
): (string | number | undefined)[] => [
  "run-environment-data-roots",
  environmentId,
];

export const useDataRoots = (
  environmentId?: number
): UseQueryResult<RunEnvironmentDataRoot[], Error> =>
  useQuery<RunEnvironmentDataRoot[], Error>({
    queryKey: dataRootsQueryKey(environmentId),
    queryFn: async () => {
      const res = await axios.get(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/data-roots`
      );
      return res.data.roots as RunEnvironmentDataRoot[];
    },
    enabled: Boolean(environmentId),
    retry: false,
  });

export const useReplaceDataRoots = (
  environmentId?: number
): UseMutationResult<
  ReplaceRunEnvironmentDataRootsResponseBody,
  unknown,
  ReplaceRunEnvironmentDataRootsRequestBody
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReplaceRunEnvironmentDataRootsRequestBody) => {
      const res = await axios.put(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/data-roots`,
        body
      );
      return res.data as ReplaceRunEnvironmentDataRootsResponseBody;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: dataRootsQueryKey(environmentId),
      });
    },
  });
};

export const useCheckDataRoots = (
  environmentId?: number
): UseMutationResult<
  CheckRunEnvironmentDataRootsResponseBody,
  unknown,
  string[]
> =>
  useMutation({
    mutationFn: async (paths: string[]) => {
      const res = await axios.post(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/data-roots/check`,
        {
          roots: paths.map(path => ({ path })),
        }
      );
      return res.data as CheckRunEnvironmentDataRootsResponseBody;
    },
  });

export const useDataRootsImpact = (
  environmentId?: number
): UseMutationResult<
  RunEnvironmentDataRootsImpactResponseBody,
  unknown,
  string[]
> =>
  useMutation({
    mutationFn: async (paths: string[]) => {
      const res = await axios.post(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/data-roots/impact`,
        { paths }
      );
      return res.data as RunEnvironmentDataRootsImpactResponseBody;
    },
  });
